import PreviewIcon from '@mui/icons-material/Preview';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { Iconify } from '@components/Iconify';
import ReactLightbox, { useController, useLightboxState } from 'yet-another-react-lightbox';

import { mergeClasses } from '@src/utils';

import { lightboxClasses } from './classes';
import { displayTotalSx, viewDetailsButtonSx, viewDetailsWrapperSx } from './styles';
import type { LightBoxProps } from './types';
import { getPlugins } from './utils';

type DisplayTotalProps = {
  totalItems: number;
  disableTotal?: boolean;
};

function DisplayTotal({ totalItems, disableTotal }: DisplayTotalProps) {
  const { currentIndex } = useLightboxState();

  if (disableTotal) {
    return null;
  }

  return (
    <Box component="span" className="yarl__button" sx={displayTotalSx}>
      <strong> {currentIndex + 1} </strong> / {totalItems}
    </Box>
  );
}

function ViewDetailsButton({ onViewDetails }: { onViewDetails: () => void }) {
  const { close } = useController();

  const handleClick = () => {
    close();
    onViewDetails();
  };

  return (
    <Box sx={viewDetailsWrapperSx}>
      <Button
        startIcon={<PreviewIcon />}
        color="primary"
        variant="contained"
        onClick={handleClick}
        sx={viewDetailsButtonSx}
        size="large"
      >
        View Details
      </Button>
    </Box>
  );
}

export function Lightbox({
  slides,
  disableZoom,
  disableVideo,
  disableTotal,
  disableCaptions,
  disableSlideshow,
  disableThumbnails,
  disableFullscreen,
  onGetCurrentIndex,
  onViewDetails,
  isFinite = false,
  className,
  ...other
}: LightBoxProps) {
  const totalItems = slides ? slides.length : 0;

  return (
    <ReactLightbox
      slides={slides}
      animation={{ swipe: 240 }}
      carousel={{ finite: isFinite ? true : totalItems < 5 }}
      controller={{ closeOnBackdropClick: true }}
      plugins={getPlugins({
        disableZoom,
        disableVideo,
        disableCaptions,
        disableSlideshow,
        disableThumbnails,
        disableFullscreen,
      })}
      on={{
        view: ({ index }: { index: number }) => {
          if (onGetCurrentIndex) {
            onGetCurrentIndex(index);
          }
        },
      }}
      toolbar={{
        buttons: [<DisplayTotal key={0} totalItems={totalItems} disableTotal={disableTotal} />, 'close'],
      }}
      render={{
        ...(onViewDetails && {
          slideFooter: () => <ViewDetailsButton onViewDetails={onViewDetails} />,
        }),
        iconClose: () => <Iconify width={24} icon="carbon:close" />,
        iconZoomIn: () => <Iconify width={24} icon="carbon:zoom-in" />,
        iconZoomOut: () => <Iconify width={24} icon="carbon:zoom-out" />,
        iconSlideshowPlay: () => <Iconify width={24} icon="carbon:play" />,
        iconSlideshowPause: () => <Iconify width={24} icon="carbon:pause" />,
        iconPrev: () => <Iconify width={32} icon="carbon:chevron-left" />,
        iconNext: () => <Iconify width={32} icon="carbon:chevron-right" />,
        iconExitFullscreen: () => <Iconify width={24} icon="carbon:center-to-fit" />,
        iconEnterFullscreen: () => <Iconify width={24} icon="carbon:fit-to-screen" />,
      }}
      className={mergeClasses([lightboxClasses.root, className])}
      {...other}
    />
  );
}
